import React, { useState, useEffect } from 'react';
import { database, db } from '../firebase';
import { ref, onValue, get, update } from 'firebase/database';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import '../styles/AdminPanel.css';

const AdminAssignProfilePanel = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [jerarquia, setJerarquia] = useState('');
    const [cuerpoServicio, setCuerpoServicio] = useState('');
    const [especialidad, setEspecialidad] = useState('');
    const [metadata, setMetadata] = useState({ cuerpos: [], especialidades: {}, grados: [] });
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        setLoading(true);
        const metadataRef = ref(database, 'app_metadata');
        const premiumRef = ref(database, 'licencesPremium');

        get(metadataRef).then((snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                setMetadata({
                    cuerpos: data.cuerpos_y_servicios ? Object.values(data.cuerpos_y_servicios) : [],
                    especialidades: data.especialidades || {},
                    grados: data.grados ? Object.values(data.grados) : []
                });
            }
        });

        const unsub = onValue(premiumRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const userList = Object.entries(data)
                    .filter(([, val]) => val && (val.email || val.userId))
                    .map(([key, val]) => ({
                        key,
                        email: val.email || key,
                        userId: val.userId || key,
                        ...val
                    }));
                setUsers(userList);
            }
            setLoading(false);
        });

        return () => unsub();
    }, []);

    const handleSelectUser = async (user) => {
        setSelectedUser(user);
        setMessage('');
        try {
            const userDoc = await getDoc(doc(db, 'users', user.userId));
            if (userDoc.exists()) {
                const data = userDoc.data();
                setJerarquia(data.jerarquia || data.grado || '');
                setCuerpoServicio(data.cuerpoServicio || '');
                setEspecialidad(data.especialidad || '');
            } else {
                setJerarquia('');
                setCuerpoServicio('');
                setEspecialidad('');
            }
        } catch (e) {
            console.error('Error loading user doc:', e);
        }
    };

    const handleSave = async () => {
        if (!selectedUser) return;
        if (!jerarquia || !cuerpoServicio) {
            setMessage('⚠️ Jerarquía y Cuerpo/Servicio son obligatorios');
            return;
        }
        setSaving(true);
        setMessage('');
        try {
            await setDoc(doc(db, 'users', selectedUser.userId), {
                email: selectedUser.email,
                jerarquia,
                grado: jerarquia,
                cuerpoServicio,
                especialidad,
                lastOperationalUpdate: serverTimestamp()
            }, { merge: true });

            await update(ref(database, `users/${selectedUser.userId}`), {
                jerarquia,
                grado: jerarquia,
                cuerpoServicio,
                especialidad,
                lastUpdated: Date.now()
            });

            setMessage('✅ Perfil asignado correctamente');
        } catch (e) {
            setMessage('❌ Error: ' + e.message);
        }
        setSaving(false);
    };

    const decodeKey = (key) => {
        if (!key) return '';
        return key.replace(/_at_/g, '@').replace(/_dot_/g, '.');
    };

    const filteredUsers = users.filter(u => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        const email = (u.email || '').toLowerCase();
        const uid = (u.userId || '').toLowerCase();
        return email.includes(term) || uid.includes(term);
    });

    if (loading) {
        return <div className="admin-loading">Cargando usuarios...</div>;
    }

    return (
        <div className="admin-panel-section" style={{ padding: '20px' }}>
            <h2>Asignar Perfil a Usuario</h2>
            <p style={{ color: '#aaa', marginBottom: '20px' }}>
                Selecciona un usuario del nodo licencesPremium y asígnale jerarquía, cuerpo/servicio y especialidad.
            </p>

            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                {/* Lista de usuarios */}
                <div style={{ flex: '1 1 400px', minWidth: '300px' }}>
                    <input
                        type="text"
                        placeholder="Buscar por email o UID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            width: '100%', padding: '10px', marginBottom: '10px',
                            borderRadius: '8px', border: '1px solid #444', background: '#0a1a2a', color: '#fff'
                        }}
                    />
                    <div style={{
                        maxHeight: '500px', overflowY: 'auto', border: '1px solid #333',
                        borderRadius: '8px', background: '#0f2438'
                    }}>
                        {filteredUsers.length === 0 ? (
                            <div style={{ padding: '20px', color: '#888', textAlign: 'center' }}>
                                No se encontraron usuarios
                            </div>
                        ) : filteredUsers.map((user) => (
                            <div
                                key={user.key}
                                onClick={() => handleSelectUser(user)}
                                style={{
                                    padding: '12px 15px', cursor: 'pointer',
                                    borderBottom: '1px solid #333',
                                    background: selectedUser?.key === user.key ? '#0f3460' : 'transparent',
                                    color: selectedUser?.key === user.key ? '#fff' : '#ccc',
                                    transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => { if (selectedUser?.key !== user.key) e.currentTarget.style.background = '#1a2744'; }}
                                onMouseLeave={(e) => { if (selectedUser?.key !== user.key) e.currentTarget.style.background = 'transparent'; }}
                            >
                                <div style={{ fontWeight: 'bold' }}>{user.email}</div>
                                <div style={{ fontSize: '0.85em', color: '#888' }}>UID: {user.userId}</div>
                                {user.premiumEndDate === 0 && (
                                    <span style={{ fontSize: '0.8em', color: '#4CAF50' }}>🔒 Permanente</span>
                                )}
                            </div>
                        ))}
                    </div>
                    <div style={{ marginTop: '8px', color: '#888', fontSize: '0.9em' }}>
                        Total: {users.length} usuarios
                    </div>
                </div>

                {/* Formulario de asignación */}
                <div style={{ flex: '1 1 400px', minWidth: '300px' }}>
                    {!selectedUser ? (
                        <div style={{
                            padding: '40px', textAlign: 'center', color: '#666',
                            border: '2px dashed #333', borderRadius: '8px'
                        }}>
                            Selecciona un usuario de la lista para asignarle un perfil
                        </div>
                    ) : (
                        <div style={{
                            background: '#0f2438', borderRadius: '8px',
                            padding: '20px', border: '1px solid #333'
                        }}>
                            <h3 style={{ marginTop: 0, borderBottom: '1px solid #333', paddingBottom: '10px' }}>
                                {selectedUser.email}
                            </h3>
                            <p style={{ fontSize: '0.9em', color: '#888' }}>
                                UID: {selectedUser.userId}
                            </p>

                            <div style={{ marginTop: '20px' }}>
                                <label  style={{  display: 'block',  marginBottom: '5px' }} className="adm-label">
                                    Jerarquía / Grado *
                                </label>
                                <select
                                    value={jerarquia}
                                    onChange={(e) => setJerarquia(e.target.value)}
                                    style={{
                                        width: '100%', padding: '10px', borderRadius: '8px',
                                        border: '1px solid #444', background: '#0a1a2a', color: '#fff'
                                    }}
                                >
                                    <option value="">Seleccionar jerarquía...</option>
                                    {metadata.grados.map((g) => (
                                        <option key={g} value={g}>{g}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ marginTop: '15px' }}>
                                <label  style={{  display: 'block',  marginBottom: '5px' }} className="adm-label">
                                    Cuerpo / Servicio *
                                </label>
                                <select
                                    value={cuerpoServicio}
                                    onChange={(e) => {
                                        setCuerpoServicio(e.target.value);
                                        setEspecialidad('');
                                    }}
                                    style={{
                                        width: '100%', padding: '10px', borderRadius: '8px',
                                        border: '1px solid #444', background: '#0a1a2a', color: '#fff'
                                    }}
                                >
                                    <option value="">Seleccionar cuerpo/servicio...</option>
                                    {metadata.cuerpos.map((c) => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ marginTop: '15px' }}>
                                <label  style={{  display: 'block',  marginBottom: '5px' }} className="adm-label">
                                    Especialidad
                                </label>
                                <select
                                    value={especialidad}
                                    onChange={(e) => setEspecialidad(e.target.value)}
                                    disabled={!cuerpoServicio || !metadata.especialidades[cuerpoServicio]}
                                    style={{
                                        width: '100%', padding: '10px', borderRadius: '8px',
                                        border: '1px solid #444', background: '#0a1a2a', color: '#fff',
                                        opacity: (!cuerpoServicio || !metadata.especialidades[cuerpoServicio]) ? 0.5 : 1
                                    }}
                                >
                                    <option value="">Seleccionar especialidad...</option>
                                    {cuerpoServicio && metadata.especialidades[cuerpoServicio]?.map((esp) => (
                                        <option key={esp} value={esp}>{esp}</option>
                                    ))}
                                </select>
                            </div>

                            {message && (
                                <div style={{
                                    marginTop: '15px', padding: '10px',
                                    borderRadius: '8px', textAlign: 'center',
                                    background: message.includes('✅') ? '#1b4332' : '#3d1f1f',
                                    color: message.includes('✅') ? '#4CAF50' : '#ff6b6b'
                                }}>
                                    {message}
                                </div>
                            )}

                            <button
                                onClick={handleSave}
                                disabled={saving}
                                 style={{ 
                                    marginTop: '20px',  width: '100%' }} className="adm-btn adm-btn--secondary"
                            >
                                {saving ? 'Guardando...' : 'Guardar Perfil'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminAssignProfilePanel;
